import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RecentRecommendationsPanel = ({ recommendations = [] }) => {
  const navigate = useNavigate();

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'high': return 'bg-error/10';
      case 'medium': return 'bg-warning/10';
      case 'low': return 'bg-success/10';
      default: return 'bg-muted/10';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'High Priority';
      case 'medium': return 'Medium Priority';
      case 'low': return 'Low Priority';
      default: return 'Priority';
    }
  };

  const handleViewAll = () => {
    navigate('/upgrade-recommendations');
  };

  const handleViewRecommendation = (recommendationId) => {
    navigate('/upgrade-recommendations', { state: { recommendationId } });
  };

  const handleAddToWishlist = (recommendationId) => {
    // Add to wishlist logic
    navigate('/wishlist');
  };

  if (recommendations?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">Recent Recommendations</h2>
        </div>
        
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="Zap" size={24} color="var(--color-muted-foreground)" />
          </div>
          <h3 className="font-medium text-foreground mb-2">No recommendations yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Upload a bike photo to get personalized upgrade recommendations.
          </p>
          <Button
            variant="outline"
            size="sm"
            iconName="Upload"
            iconPosition="left"
            onClick={() => navigate('/photo-upload')}
          >
            Upload Bike Photo
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Recent Recommendations</h2>
        <Button
          variant="ghost"
          size="sm"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={handleViewAll}
        >
          View All
        </Button>
      </div>
      <div className="space-y-4">
        {recommendations?.slice(0, 5)?.map((recommendation) => (
          <div key={recommendation?.id} className="bg-background border border-border rounded-lg p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                <Image
                  src={recommendation?.productImage}
                  alt={recommendation?.productName}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-medium text-foreground truncate">
                      {recommendation?.productName}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {recommendation?.category} • {recommendation?.bikeName}
                    </p>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBg(recommendation?.priority)} ${getPriorityColor(recommendation?.priority)}`}>
                    {getPriorityLabel(recommendation?.priority)}
                  </div>
                </div>

                <div className="flex items-center space-x-4 mb-3">
                  <div className="flex items-center space-x-1">
                    <Icon name="DollarSign" size={14} color="var(--color-accent)" />
                    <span className="text-sm font-medium text-accent">
                      ${recommendation?.price?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="TrendingUp" size={14} color="var(--color-success)" />
                    <span className="text-sm text-success">
                      +{recommendation?.performanceGain}% performance
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={14} color="var(--color-warning)" />
                    <span className="text-sm text-foreground">
                      {recommendation?.rating}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="default"
                    size="xs"
                    onClick={() => handleViewRecommendation(recommendation?.id)}
                  >
                    View Details
                  </Button>
                  <Button
                    variant="outline"
                    size="xs"
                    iconName="Heart"
                    onClick={() => handleAddToWishlist(recommendation?.id)}
                  >
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="ExternalLink"
                    onClick={() => window.open(recommendation?.purchaseUrl, '_blank')}
                  >
                    Buy Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {recommendations?.length > 5 && (
        <div className="mt-4 text-center">
          <Button
            variant="outline"
            onClick={handleViewAll}
            iconName="Plus"
            iconPosition="left"
          >
            View {recommendations?.length - 5} More Recommendations
          </Button>
        </div>
      )}
    </div>
  );
};

export default RecentRecommendationsPanel;